import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Cpu,
  Wrench,
  Rocket,
  Building2,
  Check,
  Phone,
  Zap,
  Sun,
  BatteryCharging,
} from 'lucide-react';
import CertificationsSection from '../../components/Certifications';
import { useT } from '../../i18n/LanguageContext';

const heroImg =
  'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxzb2xhciUyMHBhbmVsc3xlbnwwfHx8fDE3ODQ4MTY4NDJ8MA&ixlib=rb-4.1.0&q=85';

const content = {
  fr: {
    back: 'Tous nos services',
    badge: 'SERVICE · BUREAU D’ÉTUDES',
    title: 'Bureau',
    accent: 'd’études.',
    hero:
      'Ingénierie et conception spécialisées en électricité et énergies renouvelables — études techniques, dimensionnement et suivi de projets.',
    home: 'Accueil',
    services: 'Services',
    current: 'Bureau d’études',

    expertise: 'NOTRE EXPERTISE',
    expertiseTitle: 'Ce que nous réalisons pour vous',

    description:
      'GPE vous accompagne avec son bureau d’études et de conseil spécialisé dans l’électricité et les énergies renouvelables. Nous proposons des solutions aux particuliers, entreprises et collectivités pour leurs projets d’amélioration et d’optimisation de leurs locaux ou habitations. Notre objectif : développer des solutions et participer à la transition énergétique aux côtés des acteurs de l’innovation, au nom de l’environnement.',

    methodologyTitle: 'Méthodologie & accompagnement',
    methodology: [
      'Études de conception et d’exécution',
      'Modélisation et dimensionnement',
      'Accompagnement, assistance et conseils personnalisés',
    ],

    solutionsTitle: 'Nos solutions',
    solutions: [
      'Études CFO/CFA (courants forts et courants faibles)',
      'Études pour les installations photovoltaïques',
      'Études pour la mobilité électrique (IRVE)',
      'Études de chauffage et climatisation (CVC)',
      'Études d’isolation thermique et rénovation énergétique',
      'Maquette numérique (BIM)',
      'Audit énergétique (résidentiel et tertiaire)',
      'Optimisation et simulation énergétique',
    ],

    sectorsTitle: 'Secteurs d’intervention',
    sectors: [
      'Logements / Habitat',
      'Tertiaire / Logistique / Bureaux',
      'Industriel',
      'Collectivités / Mairies',
      'Agricole',
    ],

    ctaTitle: 'Un projet bureau d’études ?',
    ctaText:
      'Obtenez une étude personnalisée et un devis gratuit sous 24h.',
    quote: 'Demander un devis',
    call: 'Nous appeler',

    toolsBadge: 'OUTILS & TECHNOLOGIES',
    toolsTitle: 'Logiciels',
    toolsAccent: 'utilisés.',
    toolsText:
      'Nos études sont réalisées avec les outils professionnels de référence du secteur.',

    tools: [
      { name: 'AutoCAD', desc: 'DAO / plans 2D et 3D' },
      { name: 'Caneco BT', desc: 'Calculs et dimensionnement BT' },
      { name: 'Archelios Pro', desc: 'Simulation photovoltaïque' },
      { name: 'Revit (RVT)', desc: 'Maquette numérique BIM' },
      { name: 'ELIE', desc: 'Bilans énergétiques' },
    ],

    relatedTitle: 'Services complémentaires',
    related: [
      {
        slug: 'courants-forts',
        title: 'Courants forts & faibles',
        desc: 'Installations électriques tertiaires et industrielles.',
        icon: Zap,
      },
      {
        slug: 'energies-renouvelables',
        title: 'Énergies renouvelables',
        desc: 'Solutions photovoltaïques clé en main.',
        icon: Sun,
      },
      {
        slug: 'mobilite-electrique',
        title: 'Mobilité électrique',
        desc: 'Déploiement d’infrastructures de recharge (IRVE).',
        icon: BatteryCharging,
      },
    ],
  },

  en: {
    back: 'All our services',
    badge: 'SERVICE · ENGINEERING OFFICE',
    title: 'Engineering',
    accent: 'office.',
    hero:
      'Specialised engineering and design in electricity and renewable energy — technical studies, sizing and project supervision.',
    home: 'Home',
    services: 'Services',
    current: 'Engineering office',

    expertise: 'OUR EXPERTISE',
    expertiseTitle: 'What we deliver for you',

    description:
      'GPE supports you through its engineering and consulting office specialised in electricity and renewable energy. We provide solutions for individuals, businesses and public authorities for projects aimed at improving and optimising their buildings and facilities. Our objective is to develop practical solutions and contribute to the energy transition alongside innovation stakeholders, with environmental responsibility at the core.',

    methodologyTitle: 'Methodology & support',
    methodology: [
      'Design and execution studies',
      'Modelling and sizing',
      'Personalised support, assistance and consulting',
    ],

    solutionsTitle: 'Our solutions',
    solutions: [
      'Power and low-voltage electrical studies',
      'Photovoltaic installation studies',
      'Electric vehicle charging infrastructure studies',
      'Heating and air-conditioning studies (HVAC)',
      'Thermal insulation and energy renovation studies',
      'Digital modelling (BIM)',
      'Residential and commercial energy audits',
      'Energy optimisation and simulation',
    ],

    sectorsTitle: 'Industries we serve',
    sectors: [
      'Residential / Housing',
      'Commercial / Logistics / Offices',
      'Industrial',
      'Public authorities / Municipalities',
      'Agricultural',
    ],

    ctaTitle: 'Have an engineering project?',
    ctaText:
      'Get a personalised study and a free quotation within 24 hours.',
    quote: 'Request a quotation',
    call: 'Call us',

    toolsBadge: 'TOOLS & TECHNOLOGIES',
    toolsTitle: 'Software',
    toolsAccent: 'we use.',
    toolsText:
      'Our studies are carried out using professional industry-standard tools.',

    tools: [
      { name: 'AutoCAD', desc: '2D and 3D CAD drawings' },
      { name: 'Caneco BT', desc: 'LV calculations and sizing' },
      { name: 'Archelios Pro', desc: 'Photovoltaic simulation' },
      { name: 'Revit (RVT)', desc: 'BIM digital modelling' },
      { name: 'ELIE', desc: 'Energy assessments' },
    ],

    relatedTitle: 'Related services',
    related: [
      {
        slug: 'courants-forts',
        title: 'Power & low-voltage systems',
        desc: 'Commercial and industrial electrical installations.',
        icon: Zap,
      },
      {
        slug: 'energies-renouvelables',
        title: 'Renewable energy',
        desc: 'Turnkey photovoltaic solutions.',
        icon: Sun,
      },
      {
        slug: 'mobilite-electrique',
        title: 'Electric mobility',
        desc: 'EV charging infrastructure deployment.',
        icon: BatteryCharging,
      },
    ],
  },

  ar: {
    back: 'جميع خدماتنا',
    badge: 'الخدمة · مكتب الدراسات الهندسية',
    title: 'مكتب',
    accent: 'الدراسات.',
    hero:
      'الهندسة والتصميم المتخصصان في الكهرباء والطاقة المتجددة — الدراسات الفنية والحسابات ومتابعة المشاريع.',
    home: 'الرئيسية',
    services: 'الخدمات',
    current: 'مكتب الدراسات الهندسية',

    expertise: 'خبرتنا',
    expertiseTitle: 'ما نقدمه لكم',

    description:
      'ترافقكم GPE من خلال مكتب الدراسات والاستشارات المتخصص في الكهرباء والطاقة المتجددة. نقدم حلولاً للأفراد والشركات والهيئات المحلية في مشاريع تحسين وتطوير المباني والمنشآت. هدفنا هو تطوير حلول عملية والمساهمة في التحول الطاقي بالتعاون مع الجهات الفاعلة في مجال الابتكار، مع الحفاظ على البيئة.',

    methodologyTitle: 'المنهجية والمرافقة',
    methodology: [
      'دراسات التصميم والتنفيذ',
      'النمذجة والحسابات الفنية',
      'المرافقة والمساعدة والاستشارات المخصصة',
    ],

    solutionsTitle: 'حلولنا',
    solutions: [
      'دراسات التيارات القوية والضعيفة',
      'دراسات أنظمة الطاقة الشمسية',
      'دراسات البنية التحتية لشحن السيارات الكهربائية',
      'دراسات التدفئة وتكييف الهواء',
      'دراسات العزل الحراري وتجديد الطاقة',
      'النمذجة الرقمية BIM',
      'تدقيق الطاقة للمباني السكنية والتجارية',
      'تحسين ومحاكاة استهلاك الطاقة',
    ],

    sectorsTitle: 'قطاعات التدخل',
    sectors: [
      'السكن / المباني السكنية',
      'القطاع التجاري / الخدمات اللوجستية / المكاتب',
      'القطاع الصناعي',
      'الهيئات المحلية / البلديات',
      'القطاع الزراعي',
    ],

    ctaTitle: 'هل لديكم مشروع دراسات هندسية؟',
    ctaText:
      'احصلوا على دراسة مخصصة وعرض سعر مجاني خلال 24 ساعة.',
    quote: 'طلب عرض سعر',
    call: 'اتصل بنا',

    toolsBadge: 'الأدوات والتقنيات',
    toolsTitle: 'البرامج',
    toolsAccent: 'المستخدمة.',
    toolsText:
      'يتم تنفيذ دراساتنا باستخدام الأدوات والبرامج المهنية المعتمدة في القطاع.',

    tools: [
      { name: 'AutoCAD', desc: 'رسومات ثنائية وثلاثية الأبعاد' },
      { name: 'Caneco BT', desc: 'الحسابات وتصميم الجهد المنخفض' },
      { name: 'Archelios Pro', desc: 'محاكاة أنظمة الطاقة الشمسية' },
      { name: 'Revit (RVT)', desc: 'النمذجة الرقمية BIM' },
      { name: 'ELIE', desc: 'تحليل وتقييم الطاقة' },
    ],

    relatedTitle: 'الخدمات ذات الصلة',
    related: [
      {
        slug: 'courants-forts',
        title: 'التيارات القوية والضعيفة',
        desc: 'التركيبات الكهربائية للمباني التجارية والصناعية.',
        icon: Zap,
      },
      {
        slug: 'energies-renouvelables',
        title: 'الطاقات المتجددة',
        desc: 'حلول الطاقة الشمسية المتكاملة.',
        icon: Sun,
      },
      {
        slug: 'mobilite-electrique',
        title: 'التنقل الكهربائي',
        desc: 'تنفيذ البنية التحتية لشحن السيارات الكهربائية.',
        icon: BatteryCharging,
      },
    ],
  },
};

const CheckItem = ({ children }) => (
  <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-[#0b3b8c]/30 hover:shadow-sm transition-all">
    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center shrink-0">
      <Check className="w-3 h-3" strokeWidth={3.5} />
    </span>
    <span className="text-slate-700 text-sm">{children}</span>
  </div>
);

const SectionTitle = ({ Icon, children }) => (
  <div className="flex items-center gap-3">
    <span className="w-9 h-9 rounded-lg bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center">
      <Icon className="w-4 h-4" strokeWidth={2.4} />
    </span>
    <h3 className="text-[#0b1c3a] font-bold text-lg md:text-xl tracking-tight">
      {children}
    </h3>
  </div>
);

const BureauEtudes = () => {
  const { lang } = useT();

  const currentLang = ['fr', 'en', 'ar'].includes(lang) ? lang : 'fr';
  const c = content[currentLang];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      className="bg-white"
      dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
    >
      {/* HERO */}
      <section className="relative pt-40 pb-24 md:pt-48 md:pb-28 overflow-hidden">
        <img
          src={heroImg}
          alt={c.current}
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1c3a]/95 via-[#0b1c3a]/80 to-[#0b1c3a]/95" />

        <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full bg-[#0b3b8c] blur-[140px] opacity-40 pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">

          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            {c.back}
          </Link>

          <div className="mt-8 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0b3b8c] flex items-center justify-center border border-white/10">
              <Cpu className="w-6 h-6 text-white" strokeWidth={2.4} />
            </div>

            <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white/90 text-[11px] tracking-[0.25em] font-semibold">
              {c.badge}
            </span>
          </div>

          <h1 className="mt-6 text-white font-black leading-[1.05] tracking-tight text-4xl md:text-6xl lg:text-7xl max-w-4xl">
            {c.title}{' '}
            <span className="italic font-serif text-[#f5c518]">
              {c.accent}
            </span>
          </h1>

          <p className="mt-6 text-white/85 text-base md:text-lg max-w-2xl leading-relaxed">
            {c.hero}
          </p>

          <nav
            aria-label="breadcrumb"
            className="mt-8 flex items-center gap-2 text-white/70 text-sm"
          >
            <Link to="/" className="hover:text-white transition-colors">
              {c.home}
            </Link>
            <span>/</span>
            <Link to="/services" className="hover:text-white transition-colors">
              {c.services}
            </Link>
            <span>/</span>
            <span className="text-[#f5c518] font-semibold">
              {c.current}
            </span>
          </nav>
        </div>

        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white" />
      </section>

      {/* MAIN */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_360px] gap-10 lg:gap-14 items-start">

          <div>
            <span className="inline-block px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-[11px] tracking-[0.25em] font-semibold">
              {c.expertise}
            </span>

            <h2 className="mt-5 text-3xl md:text-4xl font-black text-[#0b1c3a] leading-tight tracking-tight">
              {c.expertiseTitle}
            </h2>

            <div className="mt-6 p-5 md:p-6 rounded-2xl bg-slate-50 border-l-4 border-[#0b3b8c] text-slate-700 leading-relaxed">
              {c.description}
            </div>

            {/* METHODOLOGY */}
            <div className="mt-10">
              <SectionTitle Icon={Wrench}>
                {c.methodologyTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {c.methodology.map((item) => (
                  <CheckItem key={item}>{item}</CheckItem>
                ))}
              </div>
            </div>

            {/* SOLUTIONS */}
            <div className="mt-12">
              <SectionTitle Icon={Rocket}>
                {c.solutionsTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {c.solutions.map((item) => (
                  <CheckItem key={item}>{item}</CheckItem>
                ))}
              </div>
            </div>

            {/* SECTORS */}
            <div className="mt-12">
              <SectionTitle Icon={Building2}>
                {c.sectorsTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {c.sectors.map((item) => (
                  <CheckItem key={item}>{item}</CheckItem>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <aside className="lg:sticky lg:top-28">
            <div className="relative bg-[#0b1c3a] text-white rounded-3xl p-7 shadow-xl overflow-hidden">

              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#0b3b8c] blur-[70px] opacity-60" />

              <div className="relative">

                <div className="w-11 h-11 rounded-xl bg-[#f5c518] text-[#0b1c3a] flex items-center justify-center">
                  <Cpu className="w-5 h-5" strokeWidth={2.4} />
                </div>

                <h3 className="mt-5 text-xl font-bold leading-tight">
                  {c.ctaTitle}
                </h3>

                <p className="mt-3 text-white/70 text-sm leading-relaxed">
                  {c.ctaText}
                </p>

                <Link
                  to="/contact"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#0b3b8c] hover:bg-[#082e6f] font-semibold text-sm transition-colors"
                >
                  {c.quote}
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <a
                  href="tel:+33442072262"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white text-[#0b1c3a] hover:bg-slate-100 font-semibold text-sm transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  {c.call}
                </a>

              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <CertificationsSection />

      {/* TOOLS */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-[11px] tracking-[0.25em] font-semibold">
              {c.toolsBadge}
            </span>

            <h2 className="mt-5 text-3xl md:text-4xl font-black text-[#0b1c3a] leading-tight tracking-tight">
              {c.toolsTitle}{' '}
              <span className="italic font-serif text-[#0b3b8c]">
                {c.toolsAccent}
              </span>
            </h2>

            <p className="mt-3 text-slate-600">
              {c.toolsText}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {c.tools.map((tool) => (
              <div
                key={tool.name}
                className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-[#0b3b8c]/30 hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center font-black text-sm">
                  {tool.name.charAt(0)}
                </div>

                <div className="mt-4 text-[#0b1c3a] font-bold text-sm">
                  {tool.name}
                </div>

                <div className="mt-1 text-slate-500 text-xs">
                  {tool.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED SERVICES */}
      <section className="py-20 bg-[#f7f8fb] border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <h2 className="text-2xl md:text-3xl font-black text-[#0b1c3a] tracking-tight">
            {c.relatedTitle}
          </h2>

          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {c.related.map((r) => {
              const Icon = r.icon;

              return (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  className="group bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4 hover:shadow-md hover:border-[#0b3b8c]/30 transition-all"
                >
                  <div className="shrink-0 w-11 h-11 rounded-xl bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center group-hover:bg-[#0b3b8c] group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={2.2} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="text-[#0b1c3a] font-bold text-sm">
                      {r.title}
                    </div>

                    <div className="text-slate-500 text-xs mt-1 line-clamp-1">
                      {r.desc}
                    </div>
                  </div>

                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#0b3b8c] transition-colors" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
};

export default BureauEtudes;